import { Fragment, type ReactNode } from 'react'
import {
  ArticleAppliesGroups,
  ArticleButton,
  ArticleCaseComparison,
  ArticleCaseDecision,
  ArticleCaseDecisionMaking,
  ArticleCaseLink,
  ArticleCaseMetricList,
  ArticleCallout,
  ArticleChecklist,
  ArticleDeadlineGrid,
  ArticleDecisionTree,
  ArticleFaqSection,
  ArticleKeyFacts,
  ArticlePodcastSection,
  ArticleQuote,
  ArticleSection,
  ArticleSectionIcon,
  ArticleSteps,
  ArticleTable,
  ArticleVideoSection,
} from '@/legacy/components/blog/ArticleKit'
import { RichTextRenderer } from '@/legacy/components/blog/RichTextRenderer'
import type { ArticleContentBlock, RichTextBlock } from '@/domain/article'
import { isRichTextBlock } from '@/lib/articleSections'

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)

type ArticleRenderEntry =
  | {
      type: 'richTextGroup'
      blocks: RichTextBlock[]
      indexes: number[]
    }
  | {
      type: 'block'
      block: ArticleContentBlock
      index: number
    }

const groupArticleBlocks = (blocks: ArticleContentBlock[]): ArticleRenderEntry[] =>
  blocks.reduce<ArticleRenderEntry[]>((entries, block, index) => {
    const previous = entries.at(-1)

    if (isRichTextBlock(block) && previous?.type === 'richTextGroup') {
      previous.blocks.push(block)
      previous.indexes.push(index)
      return entries
    }

    entries.push(
      isRichTextBlock(block)
        ? { type: 'richTextGroup', blocks: [block], indexes: [index] }
        : { type: 'block', block, index },
    )

    return entries
  }, [])

export const ArticleSectionRenderer = ({
  blocks,
  headingIds,
  wrapBlocks = false,
}: {
  blocks: ArticleContentBlock[]
  headingIds: Record<number, string>
  wrapBlocks?: boolean
}) => {
  const entries = groupArticleBlocks(blocks)

  return (
    <div className="article-sections">
      {entries.map((entry) => {
      const key = entry.type === 'richTextGroup' ? `rich-text-${entry.indexes[0]}` : `${entry.block.type}-${entry.index}`
      const renderBlock = (className: string, children: ReactNode) =>
        wrapBlocks ? (
          <div key={key} className={`article-section-block article-section-block--${className}`}>
            {children}
          </div>
        ) : (
          <Fragment key={key}>{children}</Fragment>
        )

      if (entry.type === 'richTextGroup') {
        const groupedHeadingIds = entry.indexes.reduce<Record<number, string>>((ids, originalIndex, groupIndex) => {
          if (headingIds[originalIndex]) ids[groupIndex] = headingIds[originalIndex]
          return ids
        }, {})

        return renderBlock('rich-text', <RichTextRenderer blocks={entry.blocks} headingIds={groupedHeadingIds} />)
      }

      const { block, index } = entry

      if (block.type === 'separator') {
        return renderBlock('separator', <hr className="article-separator" />)
      }

      if (block.type === 'section') {
        return renderBlock(
          'section',
          <ArticleSection
            id={headingIds[index] ?? block.id}
            title={block.title}
            icon={
              block.title === 'Ridge Paragon after partnering with United Accountants'
                ? <ArticleSectionIcon variant="results" />
                : undefined
            }
          >
            <RichTextRenderer blocks={block.body} className="article-kit-block-prose" />
          </ArticleSection>
        )
      }

      if (block.type === 'facts') return renderBlock('facts', <ArticleKeyFacts facts={block.facts} />)
      if (block.type === 'checklist') {
        return renderBlock(
          'checklist',
          <section id={headingIds[index]} className="article-kit-section">
            <ArticleChecklist title={block.title} items={block.items} />
          </section>
        )
      }
      if (block.type === 'timeline') return renderBlock('timeline', <ArticleDeadlineGrid items={block.items} />)
      if (block.type === 'callout') {
        return renderBlock(
          'callout',
          <ArticleCallout title={block.title} variant={block.variant}>
            <RichTextRenderer blocks={block.body} className="article-kit-block-prose" />
          </ArticleCallout>
        )
      }
      if (block.type === 'applies') {
        return renderBlock(
          'applies',
          <ArticleAppliesGroups
            id={headingIds[index]}
            groups={block.groups}
            title={block.title}
            body={block.body ? <RichTextRenderer blocks={block.body} /> : undefined}
            icon={block.title ? <ArticleSectionIcon variant="learnings" /> : undefined}
          />
        )
      }
      if (block.type === 'steps' || block.type === 'workflow') {
        return renderBlock('steps', <ArticleSteps steps={block.items} />)
      }
      if (block.type === 'table') {
        return renderBlock('table', <ArticleTable headers={block.headers} rows={block.rows.map((cells) => ({ cells }))} />)
      }
      if (block.type === 'quoteBlock') {
        return renderBlock(
          'quote',
          <ArticleQuote
            quote={block.quote}
            cite={block.cite ?? ''}
            image={block.image}
            variant={blocks[index - 1]?.type === 'applies' ? 'profile' : undefined}
          />
        )
      }
      if (block.type === 'caseComparison') {
        return renderBlock(
          'case-comparison',
          <ArticleCaseComparison
            id={headingIds[index]}
            before={block.before}
            after={block.after}
          />
        )
      }
      if (block.type === 'caseDecision') {
        return renderBlock(
          'case-decision',
          <ArticleCaseDecision
            id={headingIds[index]}
            title={block.title}
            quote={block.quote}
            cite={block.cite}
            image={block.image}
            logo={block.logo}
            logoLabel={block.logoLabel}
            concernsTitle={block.concernsTitle}
            concerns={block.concerns}
          >
            <RichTextRenderer blocks={block.body} className="article-kit-block-prose" />
          </ArticleCaseDecision>
        )
      }
      if (block.type === 'caseDecisionMaking') {
        return renderBlock(
          'case-decision-making',
          <ArticleCaseDecisionMaking
            id={headingIds[index]}
            title={block.title}
            groups={block.groups}
            examplesTitle={block.examplesTitle}
            examples={block.examples.map((example) => ({
              title: example.title,
              children: <RichTextRenderer blocks={example.body} className="article-kit-block-prose" />,
            }))}
          >
            <RichTextRenderer blocks={block.body} className="article-kit-block-prose" />
          </ArticleCaseDecisionMaking>
        )
      }
      if (block.type === 'caseMetricList') {
        return renderBlock(
          'case-metric-list',
          <ArticleCaseMetricList id={headingIds[index]} title={block.title} metrics={block.metrics} />
        )
      }
      if (block.type === 'caseLink') return renderBlock('case-link', <ArticleCaseLink href={block.href} label={block.label} />)
      if (block.type === 'decision') return renderBlock('decision', <ArticleDecisionTree items={block.items} />)
      if (block.type === 'imageBlock') {
        return renderBlock(
          'image',
          <figure className="article-kit-inline-image">
            <img src={block.image.url} alt={block.image.alternativeText ?? ''} loading="lazy" decoding="async" />
            {block.caption ? <figcaption>{block.caption}</figcaption> : null}
          </figure>
        )
      }
      if (block.type === 'video') {
        return renderBlock(
          'video',
          <ArticleVideoSection
            id={headingIds[index] ?? slugify(block.title)}
            title={block.title}
            videoTitle={block.videoTitle}
            src={block.src}
            type={block.mediaType}
            poster={block.poster}
          />
        )
      }
      if (block.type === 'podcast') {
        return renderBlock(
          'podcast',
          <ArticlePodcastSection
            id={headingIds[index] ?? slugify(block.title)}
            title={block.title}
            src={block.src}
            embedTitle={block.embedTitle}
          />
        )
      }
      if (block.type === 'faq') {
        return renderBlock(
          'faq',
          <ArticleFaqSection
            id={headingIds[index] ?? slugify(block.title)}
            title={block.title}
            icon={block.title.toLowerCase().startsWith('solution') ? <ArticleSectionIcon variant="solution" /> : undefined}
            body={block.body ? <RichTextRenderer blocks={block.body} className="article-kit-block-prose" /> : undefined}
            showControls={block.showControls}
            items={block.items.map((item) => ({
              title: item.title,
              children: <RichTextRenderer blocks={item.body} className="article-kit-block-prose" />,
            }))}
          />
        )
      }
      if (block.type === 'cta') {
        return renderBlock(
          'cta',
          <section id={headingIds[index]} className="article-kit-contact">
            <div>
              <h2>{block.title}</h2>
              <RichTextRenderer blocks={block.body} className="article-kit-block-prose" />
            </div>
            <ArticleButton href={block.href} variant="primary">
              {block.label}
            </ArticleButton>
          </section>
        )
      }

      return null
      })}
    </div>
  )
}
